'use client';

import { usePathname } from 'next/navigation';
import Cookies from 'js-cookie';
import LoaderPage from '@/components/loader-page';
import { toast } from '@/components/ui/use-toast';
import { clearAuthCookies, retrieveCustomerMe } from '@/lib/actions/customer/action';
import React from 'react';

type AuthCustomerLayoutProps = {
  children: React.ReactNode;
  isPreview?: boolean;
  checkout_url?: string;
  host: string;
};

const AUTH_PAGES = ['/account/signin', '/account/signup'];

export default function AuthCustomerLayout({
  children,
  isPreview = false,
  checkout_url,
  host,
}: AuthCustomerLayoutProps) {
  const pathname = usePathname();
  const token = Cookies.get('accessToken');

  const [isLoadingPage, setIsLoadingPage] = React.useState(true);

  React.useEffect(() => {
    if (isPreview) {
      setIsLoadingPage(false);
      return;
    }
    verifyToken();
  }, [token, pathname, isPreview]);

  const verifyToken = async () => {
    if (!token) return handleUnauthenticated();

    try {
      const res = await retrieveCustomerMe(host, token);
      res.success ? handleVerifySuccess() : handleUnauthenticated(res.message);
    } catch (error) {
      handleUnauthenticated('An unexpected error occurred.');
    }
  };

  const handleVerifySuccess = () => {
    setIsLoadingPage(false);
    if (AUTH_PAGES.includes(pathname)) {
      window.location.href = checkout_url || '/';
    }
  };

  const handleUnauthenticated = async (message?: string) => {
    setIsLoadingPage(false);
    if (!AUTH_PAGES.includes(pathname)) {
      await clearAuthCookies();
      await fetch('/api/remove-cart-cookie', { method: 'GET' });
      window.location.href = '/account/signin';
    }
    if (message) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
  };

  if (!isPreview) return <>{children}</>;
  if (isLoadingPage) return <LoaderPage text='Please wait...' />;
  return <>{children}</>;
}
